import Decimal from 'decimal.js'
import type { AdminDashboardPayments, MetricRow, ProductSalesMetric } from '../api/admin.api'

export type UserPerformanceCurrency = {
  currency: string
  salesCount: number
  processedAmount: string
  paidAmount: string
  refundedAmount: string
  chargedBackAmount: string
  chargebackRefundAmount: string
  refundAmountRate: number
  chargebackAmountRate: number
  chargebackRefundAmountRate: number
}

export type UserPerformance = {
  processedCount: number
  paidCount: number
  refundCount: number
  chargebackCount: number
  chargebackRefundCount: number
  refundCountRate: number
  chargebackCountRate: number
  chargebackRefundCountRate: number
  currencies: UserPerformanceCurrency[]
}

export type ProductSalesRankingItem = {
  productId: string
  productName: string
  processedCount: number
  currencies: Array<{ currency: string; amount: string }>
}

type CurrencyAccumulator = {
  salesCount: number
  paid: Decimal
  refunded: Decimal
  chargedBack: Decimal
  chargebackRefund: Decimal
}

function safeRate(part: number | Decimal, total: number | Decimal): number {
  const denominator = new Decimal(total)
  if (denominator.isZero()) return 0
  return new Decimal(part).div(denominator).mul(100).toNumber()
}

function rowsByCurrency(rows: MetricRow[] | undefined): Map<string, MetricRow> {
  return new Map((rows ?? []).map((row) => [row.currency, row]))
}

export function userPerformance(dashboard: AdminDashboardPayments | undefined): UserPerformance {
  const paidCount = dashboard?.payments.global.paidCount ?? 0
  const refundCount = dashboard?.payments.global.refundedCount ?? 0
  const chargebackCount = dashboard?.payments.global.chargedBackCount ?? 0
  const chargebackRefundCount = (dashboard?.payments.globalChargebackRefunds ?? []).reduce(
    (total, row) => total + row.count,
    0,
  )
  const processedCount = paidCount + refundCount + chargebackCount
  const paid = rowsByCurrency(dashboard?.payments.totalSales)
  const refunded = rowsByCurrency(dashboard?.payments.refunds)
  const chargedBack = rowsByCurrency(dashboard?.payments.chargebacks)
  const chargebackRefund = rowsByCurrency(dashboard?.payments.globalChargebackRefunds)
  const currencies = new Set([
    ...paid.keys(),
    ...refunded.keys(),
    ...chargedBack.keys(),
    ...chargebackRefund.keys(),
  ])

  const accumulator = [...currencies].map((currency): UserPerformanceCurrency => {
    const current: CurrencyAccumulator = {
      salesCount:
        (paid.get(currency)?.count ?? 0) +
        (refunded.get(currency)?.count ?? 0) +
        (chargedBack.get(currency)?.count ?? 0),
      paid: new Decimal(paid.get(currency)?.amount.amount ?? 0),
      refunded: new Decimal(refunded.get(currency)?.amount.amount ?? 0),
      chargedBack: new Decimal(chargedBack.get(currency)?.amount.amount ?? 0),
      chargebackRefund: new Decimal(chargebackRefund.get(currency)?.amount.amount ?? 0),
    }
    const processedAmount = current.paid.plus(current.refunded).plus(current.chargedBack)
    return {
      currency,
      salesCount: current.salesCount,
      processedAmount: processedAmount.toFixed(3),
      paidAmount: current.paid.toFixed(3),
      refundedAmount: current.refunded.toFixed(3),
      chargedBackAmount: current.chargedBack.toFixed(3),
      chargebackRefundAmount: current.chargebackRefund.toFixed(3),
      refundAmountRate: safeRate(current.refunded, processedAmount),
      chargebackAmountRate: safeRate(current.chargedBack, processedAmount),
      chargebackRefundAmountRate: safeRate(current.chargebackRefund, processedAmount),
    }
  })

  return {
    processedCount,
    paidCount,
    refundCount,
    chargebackCount,
    chargebackRefundCount,
    refundCountRate: safeRate(refundCount, processedCount),
    chargebackCountRate: safeRate(chargebackCount, processedCount),
    chargebackRefundCountRate: safeRate(chargebackRefundCount, processedCount),
    currencies: accumulator.sort(
      (left, right) =>
        right.salesCount - left.salesCount || left.currency.localeCompare(right.currency),
    ),
  }
}

export function productSalesRanking(
  rows: ProductSalesMetric[] | undefined,
  limit = 5,
): ProductSalesRankingItem[] {
  const products = new Map<
    string,
    { productName: string; processedCount: number; currencies: Map<string, Decimal> }
  >()

  for (const row of rows ?? []) {
    const current = products.get(row.productId) ?? {
      productName: row.productName,
      processedCount: 0,
      currencies: new Map<string, Decimal>(),
    }
    current.productName = row.productName
    current.processedCount += row.count
    current.currencies.set(
      row.currency,
      (current.currencies.get(row.currency) ?? new Decimal(0)).plus(row.amount.amount),
    )
    products.set(row.productId, current)
  }

  return [...products.entries()]
    .map(([productId, product]) => ({
      productId,
      productName: product.productName,
      processedCount: product.processedCount,
      currencies: [...product.currencies.entries()]
        .map(([currency, amount]) => ({ currency, amount: amount.toFixed(3) }))
        .sort((left, right) => left.currency.localeCompare(right.currency)),
    }))
    .sort(
      (left, right) =>
        right.processedCount - left.processedCount ||
        left.productName.localeCompare(right.productName),
    )
    .slice(0, limit)
}
